import React, { useState, useEffect, useRef } from "react";
import {
  Box, Grid, TextField, Button, Typography, Stack, Container, CircularProgress, Fade, Alert
} from "@mui/material";
import sixthBg from '../../assets/centrum_assets/design/index/sixthBG.png';
import { Email as EmailIcon, LocalPhone as PhoneIcon, Room as RoomIcon, ShoppingCart as WholesaleIcon } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import MapSection from "./franchisesectioning/MapSection";
import axiosClient from "axiosClient";
import SuccessModal from "../components/SuccessModal"; 
import ReCAPTCHA from "react-google-recaptcha"; 

const Contact = () => {
  // --- STATE ---
  const [formData, setFormData] = useState({ firstName: "", phone: "", email: "", message: "" });
  const [contactInfo, setContactInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Submission & Validation State
  const [submitting, setSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [generalError, setGeneralError] = useState(null);

  // <--- REF ---
  const recaptchaRef = useRef(null);

  useEffect(() => {
    const fetchContactDetails = async () => {
      try {
        setLoading(true);
        const response = await axiosClient.get("/contact-details");
        setContactInfo(response.data.data || {});
      } catch (error) {
        console.error("Failed to fetch contact details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContactDetails();
  }, []);

  // --- HANDLERS ---
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
        // Allow digits, plus sign, spaces, dashes, and parentheses
        if (/^[0-9+\s()-]*$/.test(value)) {
            // Limit length
            if (value.length <= 20) {
                setFormData({ ...formData, [name]: value });
            }
        }
    } else {
        setFormData({ ...formData, [name]: value });
    }

    // Clear error for this field when user types
    if (fieldErrors[name]) {
        setFieldErrors({ ...fieldErrors, [name]: null });
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // First Name
    if (!formData.firstName.trim()) {
        errors.firstName = "First name is required.";
        isValid = false;
    }

    // Phone Validation
    const phoneDigits = formData.phone.replace(/[^0-9]/g, "");
    if (!formData.phone) {
        errors.phone = "Phone number is required.";
        isValid = false;
    } else if (phoneDigits.length < 7) {
        errors.phone = "Please enter a valid phone number (at least 7 digits).";
        isValid = false;
    }

    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
        errors.email = "Email is required.";
        isValid = false;
    } else if (!emailRegex.test(formData.email)) {
        errors.email = "Please enter a valid email address.";
        isValid = false;
    }

    // Message
    if (!formData.message.trim()) {
        errors.message = "Message is required.";
        isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError(null);

    // 1. Validate
    if (!validateForm()) return;

    setSubmitting(true);

    try {
        // <--- 2. GET TOKEN ---
        const token = await recaptchaRef.current.executeAsync();

        await axiosClient.post("/contact-us", {
            name: formData.firstName,
            contact_number: formData.phone,
            email: formData.email,
            message: formData.message,
            recaptcha_token: token, 
        });

        setOpenModal(true);
        setFormData({ firstName: "", phone: "", email: "", message: "" });
        setFieldErrors({});
        
        // <--- 3. RESET CAPTCHA ---
        recaptchaRef.current.reset();

    } catch (error) {
        console.error("Submission failed:", error);
        
        const serverMsg = error.response?.data?.message;
        if (error.response?.status === 422) {
            // Handle validation errors from backend if any
            const firstError = Object.values(error.response.data.errors)[0][0];
            setGeneralError(`Submission failed: ${firstError}`);
        } else {
            setGeneralError(serverMsg || "Failed to send message. Please check your connection.");
        }

        recaptchaRef.current.reset();
    } finally {
        setSubmitting(false);
    }
  };

  const rawInfoList = [
    { icon: <EmailIcon />, title: "EMAIL", lines: [contactInfo?.email].filter(Boolean), divider: true },
    { icon: <PhoneIcon />, title: "LANDLINE", lines: [contactInfo?.landline].filter(Boolean), divider: false },
    { icon: <WholesaleIcon />, title: "WHOLESALE", lines: [contactInfo?.phone_1, contactInfo?.phone_2].filter(Boolean), divider: true },
    { icon: <RoomIcon />, title: "MAIN OFFICE", lines: [contactInfo?.address].filter(Boolean), divider: false },
  ];
  const infoList = rawInfoList.filter((item) => item.lines.length > 0);

  return (
    <>
      <PageHeader title="CONTACT" pages={[]} />
      <Box sx={{ backgroundImage: `url(${sixthBg})`, backgroundSize: "cover", px: { xs: 2, md: 6 }, py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={10} alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start", width: "100%", mt: { lg: 10, md: 5 } }}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: "bold", color: "#00b5e2", letterSpacing: 1 }}>CONNECT WITH US</Typography>
                  <Typography variant="h2" fontWeight={800} sx={{ mb: 2, mt: 1, color: "#0a1a3b", lineHeight: 1.1, letterSpacing: 0.5 }}>WRITE US A MESSAGE</Typography>
                </Box>
                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: { xs: "100%", md: "120%" }, backgroundColor: "transparent" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField 
                            name="firstName" 
                            label="First Name" 
                            fullWidth 
                            required 
                            value={formData.firstName} 
                            onChange={handleChange} 
                            disabled={submitting} 
                            error={!!fieldErrors.firstName}
                            helperText={fieldErrors.firstName}
                            sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f7f7ff", borderRadius: "6px" } }} 
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField 
                            name="phone" 
                            label="Phone Number" 
                            fullWidth 
                            required 
                            value={formData.phone} 
                            onChange={handleChange} 
                            disabled={submitting} 
                            error={!!fieldErrors.phone}
                            helperText={fieldErrors.phone}
                            sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f7f7ff", borderRadius: "6px" } }} 
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField 
                            name="email" 
                            label="Email Address" 
                            fullWidth 
                            required 
                            type="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            disabled={submitting} 
                            error={!!fieldErrors.email}
                            helperText={fieldErrors.email}
                            sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f7f7ff", borderRadius: "6px" } }} 
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField 
                            name="message" 
                            label="Message" 
                            multiline 
                            fullWidth 
                            required 
                            value={formData.message} 
                            onChange={handleChange} 
                            disabled={submitting} 
                            error={!!fieldErrors.message}
                            helperText={fieldErrors.message}
                            sx={{ "& .MuiInputBase-root": { backgroundColor: "#f8f7f7ff", minHeight: "240px", alignItems: "flex-start" } }} 
                        />
                    </Grid>
                  </Grid>

                  {generalError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {generalError}
                      </Alert>
                  )}
                  
                  <Button type="submit" variant="contained" disabled={submitting} sx={{ mt: 2, alignSelf: "flex-start", backgroundColor: "#00C4F0", borderRadius: "25px", px: 4, fontWeight: 450, letterSpacing: 2, textTransform: "uppercase", "&:hover": { backgroundColor: "#00a7d6" }, minWidth: 160, fontSize: '1.4rem' }}>
                    {submitting ? <CircularProgress size={24} color="inherit" /> : "Send Message"}
                  </Button>
                  
                  <Typography 
                    sx={{ 
                        mt: 3, 
                        color: "#666", 
                        fontSize: "1.25rem",
                        lineHeight: 1.5 
                    }}
                    >
                    This site is protected by reCAPTCHA and the Google&nbsp;
                    <a 
                        href="https://policies.google.com/privacy" 
                        target="_blank" 
                        rel="noreferrer" 
                        style={{ color: "#00C4F0", textDecoration: "none", fontWeight: "bold" }}
                    >
                        Privacy Policy
                    </a> 
                    &nbsp;and&nbsp;
                    <a 
                        href="https://policies.google.com/terms" 
                        target="_blank" 
                        rel="noreferrer" 
                        style={{ color: "#00C4F0", textDecoration: "none", fontWeight: "bold" }}
                    >
                        Terms of Service
                    </a> 
                    &nbsp;apply.
                    </Typography>

                </Box>
              </Box>
            </Grid>
            {/* Right Side Info */}
            <Grid item xs={12} md={5}>
              {loading ? (<Box display="flex" justifyContent="center" mt={10}><CircularProgress /></Box>) : (
                <Fade in={!loading} timeout={1000}>
                  <Stack spacing={5} sx={{ mt: { md: 18, lg: 23 }, ml: { md: 10 } }}>
                    {infoList.map((item, i) => (
                      <Box key={i}>
                        <Stack direction="row" spacing={3} alignItems="center">
                          <Box sx={{ color: "#8BC34A", display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: "2.2rem" } }}>{item.icon}</Box>
                          <Box>
                            <Typography sx={{ color: "#8BC34A", fontWeight: 700, fontSize: "1.7rem", lineHeight: 1.1, letterSpacing: 2 }}>{item.title}</Typography>
                            {item.lines.map((line, idx) => (<Typography key={idx} sx={{ fontWeight: 475, color: "#333", fontSize: "1.3rem", lineHeight: 1.4 }}>{line}</Typography>))}
                          </Box>
                        </Stack>
                        {item.divider && <Box sx={{ mt: 3, height: "1.25px", width: "100%", backgroundColor: "#7fccf8ff", borderRadius: "2px" }} />}
                      </Box>
                    ))}
                  </Stack>
                </Fade>
              )}
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box mt={-5}><MapSection /></Box>
      
      {/* <--- RECAPTCHA ---> */}
      <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
                 sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      />

      <SuccessModal open={openModal} onClose={() => setOpenModal(false)} title="Message Sent!" message="Thank you for contacting us. We will get back to you shortly." />
    </>
  );
};
export default Contact;